import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { ToastServiceManager } from '../../services/toast-service-manager.service';
import { take } from 'rxjs';
import { PremiseHazardTypeInputModel } from '@premise-hazard-type-models/premise-hazard-type-input.model';
import { ResponseItem } from '@models/response-item.model';
import { Units } from '@models/units.model';
import { PremiseHazardCommandsRestService } from '../../services/rest/premise-hazard-command-rest.service';
import { RoundingService } from '../../services/rounding.service';
import { GreaterThanMinimumValidator } from './validators/greater-than-minimum.validator';
import { GreaterThanValidator } from './validators/greater-than-other.validator';
import { LessThanMaximumValidator } from './validators/less-than-maximum.validator';
import { PremiseHazardType } from '@premise-hazard-type-models/premise-hazard-type.model';
import { UnitConverterService } from '../../services/unit-converter.service';
import { CurrentUnitsService } from '../../services/current-units.service';
import { BaseItemComponent } from '@app/shared/base-item.component';


@Component({
    selector: 'app-premise-hazard-type-item',
    templateUrl: './premise-hazard-type-item.component.html',
    styleUrls: ['./premise-hazard-type-item.component.scss'],
    standalone: false,
})
export class PremiseHazardTypeItemComponent extends BaseItemComponent<PremiseHazardType> implements OnInit {
  PremiseHazardTypeForm!: FormGroup;

  @Input() SelectedPremiseHazardType?: PremiseHazardType;

  private notAllRequiredFieldsError!: string;
  private successfullyCreatedText!: string;
  private successfullyUpdatedText!: string;
  private createButtonText!: string;
  private updateButtonText!: string;
  private createTitle!: string;
  private updateTitle!: string;

  public MinimumDistance: number = 5;
  public DirectHitDistanceErrorText!: string;
  public CurrentUnitsText!: string;
  public PageTitle!: string;
  public ButtonText!: string;
  public Units!: Units;

  constructor(private unitsConverterService: UnitConverterService,
    private restService: PremiseHazardCommandsRestService,
    toastServiceManager: ToastServiceManager,
    private transloco: TranslocoService,
    private roundingService: RoundingService,
    private cdr: ChangeDetectorRef,
    private unitsService: CurrentUnitsService,
  ) {
    super(toastServiceManager);
  }

  private loadTranslations(): void {
    this.transloco.selectTranslate('PremiseHazardType.Item.header.create').pipe(take(1)).subscribe(res => this.createTitle = res);
    this.transloco.selectTranslate('PremiseHazardType.Item.header.update').pipe(take(1)).subscribe(res => this.updateTitle = res);
    this.transloco.selectTranslate('Buttons.create').pipe(take(1)).subscribe(res => this.createButtonText = res);
    this.transloco.selectTranslate('Buttons.update').pipe(take(1)).subscribe(res => this.updateButtonText = res);
  }

  ngOnInit(): void {
    this.Units = this.unitsService.getCurrentUnits();
    this.IsCreating = !(this.SelectedPremiseHazardType);
    this.initializeForm();
    this.loadTranslations();
    this.transloco.selectTranslate(`Units.${Units[this.Units].toLowerCase()}`).pipe(take(1)).subscribe(res => this.CurrentUnitsText = res);
    this.transloco.selectTranslate('PremiseHazardType.error.incomplete').pipe(take(1)).subscribe(res => this.notAllRequiredFieldsError = res);
    this.transloco.selectTranslate('PremiseHazardType.Item.toast.create').pipe(take(1)).subscribe(res => this.successfullyCreatedText = res);
    this.transloco.selectTranslate('PremiseHazardType.Item.toast.update').pipe(take(1)).subscribe(res => this.successfullyUpdatedText = res);
    if (this.IsCreating) {
      this.PageTitle = this.createTitle;
      this.ButtonText = this.createButtonText;
    } else {
      this.PageTitle = this.updateTitle;
      this.ButtonText = this.updateButtonText;
    }

    if (this.SelectedPremiseHazardType) {
      this.setFormValues();
    }
  }

  private initializeForm(): void {
    let initialDirectHitDistance: number = this.roundingService.Round(this.unitsConverterService.fromMeters(5, this.Units), this.Units);
    this.MinimumDistance = initialDirectHitDistance;

    this.PremiseHazardTypeForm = new FormGroup({
      code: new FormControl<string | undefined>(undefined, [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      description: new FormControl<string | undefined>(undefined, []),
      directHitDistance: new FormControl<number>(initialDirectHitDistance, [Validators.required, GreaterThanMinimumValidator(this.Units, this.unitsConverterService), LessThanMaximumValidator(this.Units, this.unitsConverterService)]),
      innerProximityDistance: new FormControl<number | undefined>(undefined, [Validators.required, GreaterThanMinimumValidator(this.Units, this.unitsConverterService), GreaterThanValidator('directHitDistance'), LessThanMaximumValidator(this.Units, this.unitsConverterService)]),
      outerProximityDistance: new FormControl<number | undefined>(undefined, [Validators.required, GreaterThanMinimumValidator(this.Units, this.unitsConverterService), GreaterThanValidator('innerProximityDistance'), LessThanMaximumValidator(this.Units, this.unitsConverterService)]),
      displayOrder: new FormControl<number | undefined>(undefined, []),
      tearAndRunOrder: new FormControl<number | undefined>(undefined, []),
      directHitAudio: new FormControl<boolean>(false, []),
      directHitVisual: new FormControl<boolean>(false, []),
      innerProximityAudio: new FormControl<boolean>(false, []),
      innerProximityVisual: new FormControl<boolean>(false, []),
      outerProximityAudio: new FormControl<boolean>(false, []),
      outerProximityVisual: new FormControl<boolean>(false, []),
    })
  }

  private setFormValues(): void {
    if (!this.SelectedPremiseHazardType) {
      return;
    }

    const directHitDistance = this.roundingService.Round(
      this.unitsConverterService.fromMeters(this.SelectedPremiseHazardType.directHitDistance ?? 5, this.Units),
      this.Units
    );
    const innerProximityDistance = this.roundingService.Round(
      this.unitsConverterService.fromMeters(this.SelectedPremiseHazardType.innerProximityDistance, this.Units),
      this.Units
    );
    const outerProximityDistance = this.roundingService.Round(
      this.unitsConverterService.fromMeters(this.SelectedPremiseHazardType.outerProximityDistance, this.Units),
      this.Units
    );

    this.PremiseHazardTypeForm.patchValue({
      code: this.SelectedPremiseHazardType.code,
      description: this.SelectedPremiseHazardType.description,
      directHitDistance: directHitDistance,
      innerProximityDistance: innerProximityDistance,
      outerProximityDistance: outerProximityDistance,
      displayOrder: this.SelectedPremiseHazardType.displayOrder,
      tearAndRunOrder: this.SelectedPremiseHazardType.tearAndRunOrder,
      directHitAudio: this.SelectedPremiseHazardType.directHitAudio,
      directHitVisual: this.SelectedPremiseHazardType.directHitVisual,
      innerProximityAudio: this.SelectedPremiseHazardType.innerProximityAudio,
      innerProximityVisual: this.SelectedPremiseHazardType.innerProximityVisual,
      outerProximityAudio: this.SelectedPremiseHazardType.outerProximityAudio,
      outerProximityVisual: this.SelectedPremiseHazardType.outerProximityVisual,
    });
    this.cdr.detectChanges();
  }

  public onSubmit(): void {
    if (!this.beginSubmit(this.PremiseHazardTypeForm, this.notAllRequiredFieldsError)) {
      return;
    }
    let premiseHazardType = this.PremiseHazardTypeForm.value;
    let premiseHazardTypeInput: PremiseHazardTypeInputModel = {
      Code: premiseHazardType.code,
      Description: premiseHazardType.description,
      DirectHitDistance: premiseHazardType.directHitDistance,
      InnerProximityDistance: premiseHazardType.innerProximityDistance,
      OuterProximityDistance: premiseHazardType.outerProximityDistance,
      DisplayOrder: premiseHazardType.displayOrder,
      TearAndRunOrder: premiseHazardType.tearAndRunOrder,
      DirectHitAudio: premiseHazardType.directHitAudio,
      DirectHitVisual: premiseHazardType.directHitVisual,
      InnerProximityAudio: premiseHazardType.innerProximityAudio,
      InnerProximityVisual: premiseHazardType.innerProximityVisual,
      OuterProximityAudio: premiseHazardType.outerProximityAudio,
      OuterProximityVisual: premiseHazardType.outerProximityVisual,
      Units: this.Units,
    };

    const response$ = this.IsCreating
      ? this.restService.createPremiseHazardType(premiseHazardTypeInput)
      : this.restService.updatePremiseHazardType(this.SelectedPremiseHazardType!.id, premiseHazardTypeInput, this.ETag!);

    this.handleSubmitResponse(
      response$,
      this.IsCreating ? this.successfullyCreatedText : this.successfullyUpdatedText,
    );
  }

}
